import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Chip, IconButton, Typography } from "@mui/joy";
import CloseIcon from "@mui/icons-material/Close";
import MDEditor, { commands } from "@uiw/react-md-editor";
import "@uiw/react-md-editor/markdown-editor.css";
import CustomModal from "components/common/CustomModal";
import { theme } from "styles/theme";
import {
  GuideDTO,
  GuideName,
  GuideVersionConflictError,
  listGuides,
  putGuide,
} from "api/taskGuidesApi";

interface GuideEditorModalProps {
  open: boolean;
  onClose: () => void;
}

// 마크다운 툴바에서 이미지 삽입 커맨드 제거 — 가이드는 텍스트만 다룬다.
const EDITOR_COMMANDS = commands
  .getCommands()
  .filter((c) => c.name !== "image");

const GUIDE_ORDER: GuideName[] = ["tone", "principles"];
const GUIDE_LABELS: Record<GuideName, string> = {
  tone: "톤 (tone.md)",
  principles: "제작 원칙 (principles.md)",
};
// 탭별 목적 한 줄 설명 — 어떤 가이드를 편집 중인지 살짝 안내.
const GUIDE_DESCRIPTIONS: Record<GuideName, string> = {
  tone: "어조·말투·표현 — 활동지가 어떻게 들리는가",
  principles: "제작 원칙 — 구성·흐름·분량 등 무엇을 어떻게 쓰는가",
};

function guideErrorText(e: unknown): string {
  return e instanceof Error ? e.message : "오류가 발생했어요";
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString();
}

export default function GuideEditorModal({
  open,
  onClose,
}: GuideEditorModalProps) {
  const [guides, setGuides] = useState<Partial<Record<GuideName, GuideDTO>>>(
    {}
  );
  const [drafts, setDrafts] = useState<Record<GuideName, string>>({
    tone: "",
    principles: "",
  });
  const [activeTab, setActiveTab] = useState<GuideName>("tone");
  const [loading, setLoading] = useState<boolean>(false);
  const [saving, setSaving] = useState<GuideName | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 열릴 때마다 최신 가이드 로드 (저장 없이 닫았다 다시 열면 서버 상태로 리셋).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    listGuides()
      .then((list) => {
        if (cancelled) return;
        const byName: Partial<Record<GuideName, GuideDTO>> = {};
        const nextDrafts: Record<GuideName, string> = {
          tone: "",
          principles: "",
        };
        for (const g of list) {
          byName[g.name] = g;
          nextDrafts[g.name] = g.content;
        }
        setGuides(byName);
        setDrafts(nextDrafts);
      })
      .catch((e) => {
        if (!cancelled) setError(guideErrorText(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const applyGuide = (g: GuideDTO) => {
    setGuides((prev) => ({ ...prev, [g.name]: g }));
    setDrafts((prev) => ({ ...prev, [g.name]: g.content }));
  };

  const handleSave = async (name: GuideName) => {
    const base = guides[name];
    if (!base) return;
    setSaving(name);
    setError(null);
    try {
      applyGuide(await putGuide(name, drafts[name], base.version));
    } catch (e) {
      if (e instanceof GuideVersionConflictError) {
        // 다른 editor 가 먼저 저장 — 최신본으로 갱신하고 알린다.
        applyGuide(e.current);
        setError(
          "다른 사람이 먼저 저장했어요. 최신본을 불러왔으니 다시 확인 후 저장해주세요."
        );
      } else {
        setError(guideErrorText(e));
      }
    } finally {
      setSaving(null);
    }
  };

  const active = guides[activeTab];
  const activeDraft = drafts[activeTab] ?? "";
  const isOverride = active?.source === "override";
  const savingActive = saving === activeTab;
  const dirtyActive = active ? activeDraft !== active.content : false;
  const emptyActive = activeDraft.trim().length === 0;
  const canSave = dirtyActive && !emptyActive && !savingActive && !loading;

  // 에디터가 모달 높이를 가득 채우되 넘치지 않도록 px 높이를 계산한다.
  // 퍼센트/vh 를 넘기면 하단 dragbar 그립이 비활성되므로 반드시 number(px) 로 준다.
  const editorWrapperRef = useRef<HTMLDivElement>(null);
  const [editorHeight, setEditorHeight] = useState(460);
  useLayoutEffect(() => {
    if (!open) return;
    const recompute = () => {
      const wrapper = editorWrapperRef.current;
      if (!wrapper) return;
      const wrapperTop = wrapper.getBoundingClientRect().top;
      // 다이얼로그(height 96vh, 세로 중앙정렬)의 하단 = innerHeight * 0.98.
      const dialogBottom = window.innerHeight * 0.98;
      // 에디터 아래로 남겨둘 영역: 다이얼로그 하단 패딩(32) + 액션 버튼(46)
      // + 콘텐츠 Stack 간격(24) + 에러 영역 + 안전 여백.
      const reservedBelow = 32 + 46 + 24 + (error ? 40 : 0) + 12;
      const next = Math.floor(dialogBottom - wrapperTop - reservedBelow);
      setEditorHeight(Math.max(280, next));
    };
    recompute();
    window.addEventListener("resize", recompute);
    return () => window.removeEventListener("resize", recompute);
  }, [open, error, activeTab]);

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      title={
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "8px",
          }}
        >
          <Box component="span">가이드 편집</Box>
          <IconButton
            variant="plain"
            size="sm"
            color="neutral"
            aria-label="닫기"
            onClick={onClose}
            sx={{ minHeight: "32px", minWidth: "32px", borderRadius: "8px" }}
          >
            <CloseIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      }
      subtitle="에이전트가 참고하는 톤·제작 원칙 가이드입니다. 저장하면 다음 작성/검토 턴부터 반영됩니다."
      dialogProps={{
        sx: {
          width: "96vw",
          maxWidth: "96vw",
          height: "96vh",
          maxHeight: "96vh",
        },
      }}
      actions={[
        {
          label: "저장",
          variant: "Solid",
          color: "primary",
          onClick: () => handleSave(activeTab),
          buttonProps: { loading: savingActive, disabled: !canSave },
        },
      ]}
    >
      {/* 톤/구조 탭 — 워크스페이스의 편집/미리보기/코드 탭과 동일 스타일 */}
      <Box
        sx={{
          display: "flex",
          gap: "4px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          mb: "12px",
        }}
      >
        {GUIDE_ORDER.map((name) => {
          const isActive = activeTab === name;
          return (
            <Box
              key={name}
              onClick={() => setActiveTab(name)}
              sx={{
                px: "10px",
                py: "6px",
                cursor: "pointer",
                borderBottom: `2px solid ${
                  isActive ? theme.vars.palette.Content.Strong : "transparent"
                }`,
                color: isActive
                  ? theme.vars.palette.Content.Strong
                  : theme.vars.palette.Content.Weak,
                fontWeight: isActive ? 700 : 500,
                fontSize: "14px",
                userSelect: "none",
              }}
            >
              {GUIDE_LABELS[name]}
            </Box>
          );
        })}
      </Box>

      <Typography
        level="body-xs"
        sx={{ color: theme.vars.palette.Content.Medium, mb: "8px" }}
      >
        {GUIDE_DESCRIPTIONS[activeTab]}
      </Typography>

      {/* 상태 칩 */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          minHeight: "24px",
          mb: "8px",
        }}
      >
        <Chip
          size="sm"
          variant="soft"
          color={isOverride ? "primary" : "neutral"}
        >
          {isOverride ? "수정됨" : "기본값"}
        </Chip>
        {active?.updated_at && (
          <Typography
            level="body-xs"
            sx={{ color: theme.vars.palette.Content.Medium }}
          >
            마지막 저장 {formatTime(active.updated_at)}
          </Typography>
        )}
      </Box>

      {/* 마크다운 에디터 (편집 + 라이브 미리보기) */}
      {/* height 를 px(숫자)로 둬야 하단 dragbar 그립이 유지된다 (퍼센트/vh 면 비활성). */}
      {/* 글자·툴바 아이콘 기본값이 작아 CSS 로 키운다. textarea 와 하이라이트 pre 는
          폰트/행간을 동일하게 맞춰야 커서 정렬이 어긋나지 않는다. */}
      <Box
        ref={editorWrapperRef}
        data-color-mode="light"
        sx={{
          "& .w-md-editor-toolbar svg": { width: 18, height: 18 },
          "& .w-md-editor-toolbar ul > li > button": {
            height: "auto",
            padding: "5px 6px",
          },
          "& .w-md-editor-text-pre, & .w-md-editor-text-input, & .w-md-editor-text-pre > code":
            {
              fontSize: "15px !important",
              lineHeight: "1.7 !important",
            },
          "& .wmde-markdown": { fontSize: "15px" },
        }}
      >
        <MDEditor
          value={activeDraft}
          onChange={(v) =>
            setDrafts((prev) => ({ ...prev, [activeTab]: v ?? "" }))
          }
          height={editorHeight}
          preview="live"
          commands={EDITOR_COMMANDS}
        />
      </Box>

      {error && (
        <Typography
          level="body-sm"
          sx={{ mt: "8px", color: theme.vars.palette.danger[500] }}
        >
          {error}
        </Typography>
      )}
    </CustomModal>
  );
}
